import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Shield, Save, Loader2, Users, UserCog, Briefcase, User, LayoutDashboard, Plus, X } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FEATURE_LABELS = {
  dashboard: 'Dashboard',
  employees: 'Employee Directory',
  onboarding: 'Onboarding',
  ats: 'ATS (Hiring)',
  attendance: 'Attendance',
  leaves: 'Leave Management',
  holidays: 'Holidays',
  payroll: 'Payroll',
  skills: 'Skills Matrix',
  contributions: 'Contributions',
  'ai-assistant': 'AI Assistant',
  okrs: 'OKRs & Goals',
  assets: 'Assets',
  expenses: 'Expenses',
  benefits: 'Benefits',
  research: 'Research & Patents',
  learning: 'Learning',
  letters: 'Letters',
  reports: 'Reports',
  travel: 'Travel',
  settings: 'Settings',
  rbac: 'Access Control',
  // Engagement
  engage: 'Engage (Praise + Surveys)',
  helpdesk: 'Helpdesk',
  loans: 'Loans & Advances',
  witties: 'Witties (Gamified)',
  'witties-manage': 'Witties — Manage Store (Add Item)',
  wishes: 'Wishes & Greetings',
  gopal: 'Gopal (Counseling)',
  compliance: 'Compliance Training',
  // Performance & Org
  performance: 'Performance & Hiring',
  'team-kernel': 'Team Kernel',
  billing: 'Billing & Invoicing',
  wordle: 'Daily Wordle',
  insights: 'Weekly Insights Digest',
  programs: 'Engagement Programs',
  internships: 'Internships',
  'exit-management': 'Exit Management',
};

const FEATURE_GROUPS = [
  { label: 'Core', features: ['dashboard', 'settings'] },
  { label: 'People', features: ['employees', 'onboarding', 'ats', 'internships', 'exit-management', 'team-kernel'] },
  { label: 'Time & Leave', features: ['attendance', 'leaves', 'holidays'] },
  { label: 'Finance', features: ['payroll', 'expenses', 'benefits', 'loans', 'billing'] },
  { label: 'Growth', features: ['skills', 'contributions', 'okrs', 'learning', 'research', 'performance'] },
  { label: 'Engagement', features: ['engage', 'programs', 'witties', 'witties-manage', 'wishes', 'gopal', 'helpdesk', 'compliance', 'wordle'] },
  { label: 'Tools', features: ['ai-assistant', 'assets', 'letters', 'reports', 'travel', 'insights'] },
  { label: 'Admin', features: ['rbac'] },
];

const ROLE_CONFIG = {
  admin: { label: 'Admin / Founder', icon: Shield, color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' },
  hr: { label: 'HR', icon: UserCog, color: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400' },
  manager: { label: 'Pilot', icon: Briefcase, color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' },
  employee: { label: 'CoPilot', icon: User, color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' },
};

const RBACPage = () => {
  const [permissions, setPermissions] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [persona, setPersona] = useState({});
  const [savingPersona, setSavingPersona] = useState(null);

  const fetchPermissions = useCallback(async () => {
    try {
      const [permRes, personaRes] = await Promise.all([
        axios.get(`${API_URL}/api/rbac/permissions`, { withCredentials: true }),
        axios.get(`${API_URL}/api/persona/dashboard/all`, { withCredentials: true }),
      ]);
      setPermissions(permRes.data);
      setPersona(personaRes.data);
    } catch (err) {
      toast.error('Failed to load permissions');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchPermissions(); }, [fetchPermissions]);

  const togglePermission = (role, feature) => {
    if (role === 'admin') return; // Admin always has all access
    setPermissions(prev => ({
      ...prev,
      [role]: { ...prev[role], [feature]: !prev[role][feature] }
    }));
  };

  const saveRole = async (role) => {
    setSaving(role);
    try {
      await axios.put(`${API_URL}/api/rbac/permissions/${role}`, permissions[role], { withCredentials: true });
      toast.success(`${ROLE_CONFIG[role].label} permissions saved`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const roles = ['admin', 'hr', 'manager', 'employee'];

  return (
    <div className="space-y-6 animate-fade-in" data-testid="rbac-page">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Shield className="w-7 h-7 text-primary" />
          Access Control Centre
        </h1>
        <p className="text-muted-foreground">Configure sidebar visibility and feature access for each role</p>
      </div>

      {/* Role Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {roles.map(role => {
          const cfg = ROLE_CONFIG[role];
          const Icon = cfg.icon;
          const enabledCount = permissions[role] ? Object.values(permissions[role]).filter(Boolean).length : 0;
          const totalCount = Object.keys(FEATURE_LABELS).length;
          return (
            <Card key={role} className="text-center" data-testid={`role-card-${role}`}>
              <CardContent className="pt-5 pb-4">
                <div className={`inline-flex p-2.5 rounded-lg ${cfg.color} mb-2`}>
                  <Icon className="w-5 h-5" />
                </div>
                <p className="font-semibold text-sm">{cfg.label}</p>
                <p className="text-xs text-muted-foreground">{enabledCount}/{totalCount} features</p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Permission Matrix */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="w-5 h-5" />
            Permission Matrix
          </CardTitle>
          <CardDescription>Toggle features on/off for each role. Admin always has full access.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-3 px-3 font-semibold min-w-[180px]">Feature</th>
                  {roles.map(role => (
                    <th key={role} className="text-center py-3 px-2 font-semibold min-w-[120px]">
                      <Badge variant="outline" className={ROLE_CONFIG[role].color}>
                        {ROLE_CONFIG[role].label}
                      </Badge>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {FEATURE_GROUPS.map(group => (
                  <React.Fragment key={group.label}>
                    <tr>
                      <td colSpan={5} className="pt-4 pb-1 px-3 text-xs font-bold uppercase tracking-wider text-muted-foreground">{group.label}</td>
                    </tr>
                    {group.features.map(feature => (
                      <tr key={feature} className="border-b border-border/50 hover:bg-muted/30 transition-colors">
                        <td className="py-2.5 px-3 font-medium">{FEATURE_LABELS[feature]}</td>
                        {roles.map(role => (
                          <td key={role} className="text-center py-2.5 px-2">
                            <Switch
                              checked={permissions[role]?.[feature] ?? false}
                              onCheckedChange={() => togglePermission(role, feature)}
                              disabled={role === 'admin'}
                              data-testid={`toggle-${role}-${feature}`}
                            />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>

          {/* Save Buttons */}
          <div className="flex gap-3 mt-6 pt-4 border-t justify-end">
            {roles.filter(r => r !== 'admin').map(role => (
              <Button
                key={role}
                onClick={() => saveRole(role)}
                disabled={saving === role}
                variant="outline"
                className="gap-2"
                data-testid={`save-${role}-btn`}
              >
                {saving === role ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Save {ROLE_CONFIG[role].label}
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>
      {/* Persona-Based Dashboard Storyboards */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><LayoutDashboard className="w-5 h-5" /> Persona-Based Storyboards</CardTitle>
          <CardDescription>Configure which dashboard tiles each role sees on their home dashboard. Drag to remove or click + to add.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {roles.map(role => {
              const tiles = persona[role] || [];
              const allTiles = ['employees', 'active_employees', 'pending_leaves', 'pending_expenses', 'pending_travel', 'monthly_payroll', 'tech_stack', 'team_attendance', 'ai_assistant', 'performance_overview', 'open_requisitions', 'wordle_streak', 'my_attendance', 'my_leaves', 'my_payslip', 'my_okrs', 'team_okrs', 'witties_balance'];
              const available = allTiles.filter(t => !tiles.includes(t));
              const handleSave = async () => {
                setSavingPersona(role);
                try {
                  await axios.put(`${API_URL}/api/persona/dashboard/${role}`, { tiles }, { withCredentials: true });
                  toast.success(`Storyboard saved for ${ROLE_CONFIG[role].label}`);
                } catch (err) { toast.error('Failed'); }
                finally { setSavingPersona(null); }
              };
              return (
                <Card key={role} data-testid={`persona-${role}`}>
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <Badge className={ROLE_CONFIG[role].color}>{ROLE_CONFIG[role].label}</Badge>
                      <Button size="sm" variant="outline" className="gap-1 h-7 text-xs" onClick={handleSave} disabled={savingPersona === role} data-testid={`save-persona-${role}`}>
                        {savingPersona === role ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Save
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <div className="flex flex-wrap gap-1.5 min-h-[40px] p-2 rounded border bg-muted/20">
                      {tiles.length > 0 ? tiles.map(t => (
                        <Badge key={t} variant="secondary" className="gap-1 capitalize text-xs">
                          {t.replace(/_/g, ' ')}
                          <button onClick={() => setPersona(p => ({ ...p, [role]: p[role].filter(x => x !== t) }))} className="hover:text-destructive" data-testid={`persona-remove-${role}-${t}`}>
                            <X className="w-3 h-3" />
                          </button>
                        </Badge>
                      )) : <span className="text-xs text-muted-foreground italic">No tiles selected</span>}
                    </div>
                    {available.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {available.slice(0, 8).map(t => (
                          <button key={t} onClick={() => setPersona(p => ({ ...p, [role]: [...(p[role] || []), t] }))} className="text-[10px] px-2 py-0.5 rounded border border-dashed hover:bg-muted capitalize" data-testid={`persona-add-${role}-${t}`}>
                            <Plus className="w-2.5 h-2.5 inline mr-0.5" />{t.replace(/_/g, ' ')}
                          </button>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Per-app SSO access — Cortex, etc. */}
      <AppAccessCard />
    </div>
  );
};

// ============================================================
// AppAccessCard — per-user toggles for cortex.wisewit.ai etc.
// ============================================================
const AppAccessCard = () => {
  const [data, setData] = React.useState({ users: [], apps: [] });
  const [loading, setLoading] = React.useState(true);
  const [filter, setFilter] = React.useState('');

  const fetchAccess = React.useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/sso/app-access`, { withCredentials: true });
      setData(res.data);
    } catch (err) {
      toast.error('Failed to load app access');
    } finally { setLoading(false); }
  }, []);

  React.useEffect(() => { fetchAccess(); }, [fetchAccess]);

  const toggle = async (userId, app, current) => {
    try {
      await axios.put(`${API_URL}/api/sso/app-access/${userId}`, { app, access: !current }, { withCredentials: true });
      toast.success(`${app} access ${current ? 'revoked' : 'granted'}`);
      setData(d => ({ ...d, users: d.users.map(u => u.id === userId ? { ...u, app_access: { ...u.app_access, [app]: !current } } : u) }));
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const removeUser = async (userId, userName) => {
    if (!window.confirm(`Remove ${userName || 'this user'} from the access list? This revokes all app access and archives their account. Undo requires re-hiring.`)) return;
    try {
      await axios.delete(`${API_URL}/api/sso/app-access/${userId}`, { withCredentials: true });
      toast.success(`${userName || 'User'} removed from access list`);
      setData(d => ({ ...d, users: d.users.filter(u => u.id !== userId) }));
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to remove'); }
  };

  const filtered = data.users.filter(u => !filter || (u.name + u.email).toLowerCase().includes(filter.toLowerCase()));

  return (
    <Card data-testid="app-access-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><Briefcase className="w-5 h-5" /> Sister-App Access</CardTitle>
        <CardDescription>Revoke a specific sister app (e.g., cortex.wisewit.ai) without disabling the HR account. Ex-employees (archived/relieved) are auto-hidden. Use <b>Remove</b> for stragglers.</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="mb-3">
          <input type="text" placeholder="Filter by name or email…" value={filter} onChange={e => setFilter(e.target.value)} className="w-full md:w-72 text-sm p-2 rounded border bg-background" data-testid="app-access-filter" />
        </div>
        {loading ? (
          <div className="flex items-center justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="data-table w-full text-sm">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Role</th>
                  {data.apps.map(a => <th key={a} className="capitalize text-center">{a}</th>)}
                  <th className="text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length > 0 ? filtered.map(u => (
                  <tr key={u.id} data-testid={`app-access-row-${u.id}`}>
                    <td>
                      <p className="font-medium">{u.name || '—'}</p>
                      <p className="text-xs text-muted-foreground">{u.email}</p>
                    </td>
                    <td><Badge variant="outline" className="text-[10px] capitalize">{u.role}</Badge></td>
                    {data.apps.map(a => (
                      <td key={a} className="text-center">
                        <Switch
                          checked={!!u.app_access[a]}
                          onCheckedChange={() => toggle(u.id, a, u.app_access[a])}
                          data-testid={`app-access-${a}-${u.id}`}
                        />
                      </td>
                    ))}
                    <td className="text-right">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => removeUser(u.id, u.name)}
                        className="h-7 gap-1 text-xs text-rose-600 hover:text-rose-700 hover:bg-rose-50 dark:hover:bg-rose-950/30"
                        data-testid={`remove-user-${u.id}`}
                        title="Revoke all app access and archive this user"
                      >
                        <X className="w-3.5 h-3.5" /> Remove
                      </Button>
                    </td>
                  </tr>
                )) : (
                  <tr><td colSpan={3 + data.apps.length} className="text-center text-muted-foreground py-4">No users match.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default RBACPage;
